import {
  addCSFlag,
  addDevice,
  addDeviceByKit,
  addPatient,
  AddPatientParams,
  addPregnancy,
  addPrescription,
  checkBloomlifeId,
  checkResetPasswordToken,
  CheckResetPasswordTokenParams,
  checkSignUpInvitation,
  CheckSignUpInvitationParams,
  closePrescription,
  editPatientInformation,
  getCustomerSupportOverview,
  getDeviceOrderKits,
  getEMRPatientByMRN,
  getOrganizations,
  getPatientByMRN,
  GetPatientByMRNParams,
  getPatientDetailPageData,
  getPatientsOverviewPageData,
  getPrescriptionReport,
  getSelfUser,
  logInWithEmailAndPassword,
  logInWithMfaCode,
  logOut,
  offboardPrescription,
  orderDevice,
  OrderDeviceParams,
  removeCSFlag,
  reopenPrescription,
  resendInvitationEmail,
  resetPassword,
  sendForgotPasswordEmail,
  sendPatientInvitation,
  sendResetPasswordEmail,
  signUp,
  suspendDevice,
  unlinkDevice,
  unsuspendDevice,
  updateCustomerSupportNotes,
  updatePregnancy,
  updatePrescription,
  updateReminders,
} from "@/data/requests"
import { ClinicalProtocolType } from "@/model/ClinicalProtocol"
import {
  EnrollmentStatus,
  toCustomerSupportOverview,
} from "@/model/CustomerSupport"
import { Organization } from "@/model/Organization"
import {
  ImportedPatient,
  PatientApiDto,
  PatientDetailPageDataApiDto,
  PrescriptionReport,
  toImportedPatient,
  toPatient,
  toPatientDetailPageData,
  toPrescriptionReport,
} from "@/model/Patient"
import {
  DiabetesPrescriptionApiDto,
  HypertensionPrescriptionApiDto,
  Prescription,
  toPrescription,
} from "@/model/Prescription"
import {
  ReminderDayTime,
  ReminderType,
  toReminderDayTimeApiDto,
} from "@/model/Reminders"
import { toSupportUser } from "@/model/SupportUser"
import { contains } from "@/utils/string-utils"
import { Instant } from "@js-joda/core"
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"

export type PatientsOverviewPageData = {
  organizations: Organization[]
  patients: PatientApiDto[]
  prescriptions: (HypertensionPrescriptionApiDto | DiabetesPrescriptionApiDto)[]
}

export const useLogInMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logInWithEmailAndPassword,
    onSuccess: (result) => {
      if (result.type === "authenticated") queryClient.invalidateQueries()
    },
  })
}

export const useLogInWithMfaCodeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logInWithMfaCode,
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export const useLogOutMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => logOut(),
    onMutate: () => queryClient.cancelQueries(),
    onSuccess: () => {
      queryClient.clear()
      return undefined
    },
  })
}

export const useSendForgotPasswordEmailMutation = () => {
  return useMutation({
    mutationFn: sendForgotPasswordEmail,
  })
}

export const useCheckResetPasswordTokenQuery = (
  params: CheckResetPasswordTokenParams
) =>
  useQuery({
    queryKey: ["check-reset-password-token", params],
    queryFn: async () => {
      await checkResetPasswordToken(params)
      return true // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  })

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
    retry: 0,
  })
}

export const useCheckSignUpInvitationQuery = (
  params: CheckSignUpInvitationParams
) =>
  useQuery({
    queryKey: ["check-invitation", params],
    queryFn: async () => {
      await checkSignUpInvitation(params)
      return true // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  })

export const useSignUpMutation = () => {
  return useMutation({
    mutationFn: signUp,
  })
}

export const useResendInvitationEmailMutation = () => {
  return useMutation({
    mutationFn: resendInvitationEmail,
  })
}

export const useSelfUserQuery = () =>
  useQuery({
    queryKey: ["self-user"],
    queryFn: getSelfUser,
    select: toSupportUser,
  })

export const usePatientDetailPageDataQuery = (bloomlifeId: string) =>
  useQuery({
    queryKey: ["patient", bloomlifeId],
    queryFn: () => getPatientDetailPageData({ bloomlifeId }),
    select: (data) => toPatientDetailPageData(data),
    staleTime: 60_000,
  })

export const useCheckPatientMRNMutation = () =>
  useMutation({
    mutationFn: async (params: GetPatientByMRNParams) => {
      const patientDto = await getPatientByMRN(params)
      return patientDto ? toPatient(patientDto) : null
    },
  })

export const useEMRPatientQuery = ({
  organizationId,
  medicalRecordNumber,
  ...otherOptions
}: GetPatientByMRNParams &
  Omit<
    UseQueryOptions<ImportedPatient | null>,
    "queryKey" | "queryFn" | "staleTime" | "retry" | "refetchOnWindowFocus"
  >) =>
  useQuery({
    ...otherOptions,
    queryKey: ["emr-patient", organizationId, medicalRecordNumber],
    queryFn: async () => {
      const patientDto = await getEMRPatientByMRN({
        organizationId,
        medicalRecordNumber,
      })
      return patientDto ? toImportedPatient(patientDto) : null
    },
    staleTime: Infinity,
    retry: 0,
    refetchOnWindowFocus: false,
  })

export const useCheckBloomlifeIdMutation = () =>
  useMutation({
    mutationFn: checkBloomlifeId,
  })

export const useAddPatientMutation = () => {
  const queryClient = useQueryClient()
  const queryKey = ["all-organization-patients"]
  return useMutation({
    mutationFn: async (params: AddPatientParams) => {
      const patientDto = await addPatient(params)
      return toPatient(patientDto)
    },
    onMutate: async ({ patient }: AddPatientParams) => {
      await queryClient.cancelQueries({ queryKey })

      const oldPatientsData =
        queryClient.getQueryData<PatientsOverviewPageData>(queryKey)

      if (!oldPatientsData) {
        return
      }

      queryClient.setQueryData(queryKey, {
        ...oldPatientsData,
        patients: [patient, ...oldPatientsData.patients],
      })

      return () => {
        queryClient.setQueryData(queryKey, oldPatientsData)
      }
    },
    onError: (_error, _params, rollback) => {
      if (rollback) {
        rollback()
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export type StatusFilter =
  | "all"
  | "monitoring"
  | "noReadingInLast24Hours"
  | "noFirstReading"
  | "noDevices"

export type ClinicalProtocolTypeFilter = ClinicalProtocolType | "all"

export const usePatientListPageDataQuery = ({
  organizationId,
  protocol,
  filter,
  search,
}: {
  organizationId: string
  protocol: ClinicalProtocolTypeFilter
  filter: StatusFilter
  search?: string
}) =>
  useQuery({
    queryKey: ["all-organization-patients"],
    queryFn: getPatientsOverviewPageData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    gcTime: 3_600_000,
    select: (data) => {
      const organizations = data.organizations
      const patients = data.patients.map(toPatient)
      const prescriptions = data.prescriptions.map(toPrescription)

      const patientFor = (prescription: Prescription) =>
        patients.find((patient) => patient.id === prescription.patientId)!

      const filteredPrescriptionsForOrganization = prescriptions.filter(
        (prescription) =>
          organizationId === "all" ||
          prescription.organizationId === organizationId
      )

      const filteredPrescriptionsForProtocolType =
        filteredPrescriptionsForOrganization.filter(
          (prescription) => protocol === "all" || protocol === prescription.type
        )

      const protocolTypeFilterResults = {
        all: filteredPrescriptionsForOrganization,
        hypertension: filteredPrescriptionsForOrganization.filter(
          ({ type }) => type === "hypertension"
        ),
        diabetes: filteredPrescriptionsForOrganization.filter(
          ({ type }) => type === "diabetes"
        ),
      }

      const statusFilterResults = {
        all: filteredPrescriptionsForProtocolType,
        monitoring: filteredPrescriptionsForProtocolType.filter(
          ({ firstReadingTime }) => !!firstReadingTime
        ),
        noFirstReading: filteredPrescriptionsForProtocolType.filter(
          ({ firstReadingTime }) => !firstReadingTime
        ),
        noReadingInLast24Hours: filteredPrescriptionsForProtocolType.filter(
          ({ lastReadingTime }) =>
            !lastReadingTime ||
            lastReadingTime.isBefore(Instant.now().minusSeconds(24 * 60 * 60))
        ),
        noDevices: filteredPrescriptionsForProtocolType.filter(
          (prescription) =>
            prescription.type === "hypertension"
              ? !patientFor(prescription).hasBloodPressureMonitor
              : !patientFor(prescription).hasBloodGlucoseMonitor
        ),
      }

      const protocolTypeFilterSummary = {
        all: protocolTypeFilterResults.all.length,
        diabetes: protocolTypeFilterResults.diabetes.length,
        hypertension: protocolTypeFilterResults.hypertension.length,
      }

      const statusFilterSummary = {
        all: statusFilterResults.all.length,
        monitoring: statusFilterResults.monitoring.length,
        noFirstReading: statusFilterResults.noFirstReading.length,
        noReadingInLast24Hours:
          statusFilterResults.noReadingInLast24Hours.length,
        noDevices: statusFilterResults.noDevices.length,
      }

      const keysToSearch = [
        "bloomlifeId",
        "medicalRecordNumber",
        "firstName",
        "lastName",
        "phoneNumber",
      ] as const
      const filteredPrescriptions = (
        statusFilterResults[filter] ?? statusFilterResults.all
      ).filter((prescription) => {
        const patient = patientFor(prescription)
        return (
          !search ||
          keysToSearch.some(
            (key) =>
              patient[key] &&
              contains(patient[key], search, {
                ignoreCase: true,
                ignoreDiacritics: true,
              })
          )
        )
      })

      return {
        organizations: organizations,
        patients: patients.filter((patient) =>
          filteredPrescriptions.some(
            (prescription) => patient.id === prescription.patientId
          )
        ),
        prescriptions: filteredPrescriptions,
        filterSummary: {
          protocolType: protocolTypeFilterSummary,
          status: statusFilterSummary,
        },
      }
    },
  })

type UpdateRemindersParams = {
  reminderType: ReminderType
  reminders: ReminderDayTime[]
}

export const useUpdateRemindersMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: ({ reminderType, reminders }: UpdateRemindersParams) =>
      updateReminders({
        bloomlifeId,
        reminderType,
        reminders: reminders.map(toReminderDayTimeApiDto),
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useEditPatientInformationMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: editPatientInformation,
    onMutate: async ({ update }) => {
      await queryClient.cancelQueries({ queryKey })
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        patient: {
          ...patientDetailPageData.patient,
          ...update,
        },
      })
      return () => queryClient.setQueryData(queryKey, patientDetailPageData)
    },
    onError: (_error, _params, rollback) => {
      if (rollback) {
        rollback()
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useAddPatientDeviceMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: addDevice,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useAddPatientDeviceByKitMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: addDeviceByKit,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useClosePrescription = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: closePrescription,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useOffboardPrescription = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: offboardPrescription,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", bloomlifeId] })
      queryClient.invalidateQueries({ queryKey: ["customer-support-overview"] })
    },
  })
}

export const useReopenPrescription = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: reopenPrescription,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useUpdatePrescriptionEnrollmentStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      prescriptionId,
      newStatus,
    }: {
      prescriptionId: string
      newStatus: EnrollmentStatus
    }) =>
      updatePrescription({
        prescriptionId,
        update: {
          enrollmentStatus: newStatus,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-support-overview"] })
    },
  })
}

type UpdatePrescriptionParams = {
  bloomlifeId: string
}

export const useUpdatePrescriptionMutation = ({
  bloomlifeId,
}: UpdatePrescriptionParams) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: updatePrescription,
    onMutate: async ({ prescriptionId, update }) => {
      await queryClient.cancelQueries({ queryKey })
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!

      const newPrescriptions = patientDetailPageData.prescriptions.map(
        (prescription) =>
          prescription.id === prescriptionId
            ? { ...prescription, ...update }
            : prescription
      )
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        prescriptions: newPrescriptions,
      })
      return () => queryClient.setQueryData(queryKey, patientDetailPageData)
    },
    onError: (_error, _params, rollback) => {
      if (rollback) {
        rollback()
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useAddPregnancyMutation = ({
  bloomlifeId,
}: {
  bloomlifeId: string
}) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: addPregnancy,
    onSuccess: (newPregnancy) => {
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        pregnancies: [newPregnancy, ...patientDetailPageData.pregnancies],
      })
    },
  })
}

export const useUpdatePregnancyMutation = ({
  bloomlifeId,
}: {
  bloomlifeId: string
}) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: updatePregnancy,
    onMutate: async ({ pregnancyId, update }) => {
      await queryClient.cancelQueries({ queryKey })
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!

      const newPregnancies = patientDetailPageData.pregnancies.map(
        (pregnancy) =>
          pregnancy.id === pregnancyId ? { ...pregnancy, ...update } : pregnancy
      )
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        pregnancies: newPregnancies,
      })
      return () => queryClient.setQueryData(queryKey, patientDetailPageData)
    },
    onError: (_error, _params, rollback) => {
      if (rollback) {
        rollback()
      }
    },
    onSuccess: (updatedPregnancy) => {
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        pregnancies: patientDetailPageData.pregnancies.map((pregnancy) =>
          pregnancy.id === updatedPregnancy.id ? updatedPregnancy : pregnancy
        ),
      })
    },
  })
}

export const useAddPrescriptionMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: addPrescription,
    onSuccess: (newPrescription) => {
      const patientDetailPageData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)!
      queryClient.setQueryData(queryKey, {
        ...patientDetailPageData,
        prescriptions: [
          ...patientDetailPageData.prescriptions,
          newPrescription,
        ],
      })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useUnlinkPatientDeviceMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: unlinkDevice,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useSuspendPatientDeviceMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: suspendDevice,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useUnsuspendPatientDeviceMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: unsuspendDevice,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useSendPatientInvitationMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: sendPatientInvitation,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useSendResetPasswordEmailMutation = () =>
  useMutation({
    mutationFn: sendResetPasswordEmail,
  })

export const useOrganizationsQuery = () => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ["organization"],
    initialData: () =>
      queryClient.getQueryData<PatientsOverviewPageData>([
        "all-organization-patients",
      ])?.organizations,
    queryFn: getOrganizations,
    staleTime: 240_000,
    gcTime: 3_600_000,
  })
}

export const useCustomerSupportOverviewQuery = () =>
  useQuery({
    queryKey: ["customer-support-overview"],
    queryFn: async () => {
      const dto = await getCustomerSupportOverview()
      if (dto) {
        return toCustomerSupportOverview(dto)
      }
      return undefined
    },
    staleTime: 60_000,
    gcTime: 3_600_000,
  })

export const useAddCSFlagMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: addCSFlag,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-support-overview"] })
      queryClient.invalidateQueries({
        queryKey: ["customer-support-patient", bloomlifeId],
      })
      queryClient.invalidateQueries({ queryKey: ["patient", bloomlifeId] })
    },
  })
}

export const useRemoveCSFlagMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeCSFlag,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-support-overview"] })
      queryClient.invalidateQueries({
        queryKey: ["customer-support-patient", bloomlifeId],
      })
      queryClient.invalidateQueries({ queryKey: ["patient", bloomlifeId] })
    },
  })
}

export const useDeviceOrderKitsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["device-order-kits"],
    queryFn: getDeviceOrderKits,
    enabled,
  })

// TODO: Cleanup these functions when we have the actual API calls
export const useOrderDeviceMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Omit<OrderDeviceParams, "bloomlifeId">) =>
      orderDevice({ bloomlifeId, ...params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-support-overview"] })
      queryClient.invalidateQueries({
        queryKey: ["customer-support-patient", bloomlifeId],
      })
      queryClient.invalidateQueries({ queryKey: ["patient", bloomlifeId] })
    },
  })
}

export const usePatientReadingsQuery = (
  bloomlifeId: string,
  prescriptionId: string
) => {
  return useQuery<PrescriptionReport>({
    queryKey: ["patient-readings", bloomlifeId, prescriptionId],
    queryFn: async () => {
      const reportData = await getPrescriptionReport({ prescriptionId })
      return toPrescriptionReport(reportData)
    },
    staleTime: 60_000,
    gcTime: 300_000,
  })
}

export const useUpdateCustomerSupportNotesMutation = (bloomlifeId: string) => {
  const queryClient = useQueryClient()
  const queryKey = ["patient", bloomlifeId]
  return useMutation({
    mutationFn: (notes: string) =>
      updateCustomerSupportNotes({ bloomlifeId, notes }),
    onMutate: async (notes) => {
      await queryClient.cancelQueries({ queryKey })
      const previousData =
        queryClient.getQueryData<PatientDetailPageDataApiDto>(queryKey)

      if (previousData) {
        queryClient.setQueryData<PatientDetailPageDataApiDto>(queryKey, {
          ...previousData,
          patient: {
            ...previousData.patient,
            customerSupportNotes: {
              ...previousData.patient.customerSupportNotes,
              reason: previousData.patient.customerSupportNotes?.reason ?? "",
              flagged:
                previousData.patient.customerSupportNotes?.flagged ?? false,
              notes,
            },
          },
        })
      }

      return { previousData }
    },
    onError: (_error, _notes, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}
